import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import { isAdminUser, isFundingAdminUser } from 'utils';
import {
  checkUser,
  checkUserLogged,
  checkIsValidated,
  getUser,
} from 'store/user/actions';

/**
 * @summary Component acting as wrapper for protecting routes with authentication and access control.
 * @description Wraps route elements and ensures they are only accessible if the user is authenticated
 * and (optionally) has the required roles. Redirects unauthorized users to the login page
 * and authorized users to their initial loggedIn page as appropriate.
 * Error and AlreadyLoggedIn pages are not needed due to redirections.
 * @param {object} props - Component props object.
 * @param {JSX.Element} props.children - The route element (component/page) to render if access is granted.
 * @param {boolean} [props.requireAuth=false] - Whether the route requires the user to be authenticated.
 * @param {boolean} [props.requirePasswordChange=false] - Whether the route requires the user to change password. Only true on new-password.
 * @param {boolean} [props.requireConfirmRegistration=false] - Whether the route requires the user to confirm registration. Only true on confirm-registration.
 * @param {Array<string>} [props.requiredGroups=[]] - List of user roles(groups) required to access the route.
 * @returns {JSX.Element} The rendered route element if access is allowed, or a redirect if not.
 */
export function AuthGuard(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userStatus = useSelector(state => state.user.status);
  const mustChangePassword = useSelector(
    state => state.user.mustChangePassword
  );
  const groups = useSelector(state => state.user.groups);
  const checkingAuth = useSelector(state => state.user.checkingAuth);
  const loadingLocales = useSelector(state => state.forms.loadingLocales);
  const loadingUser = useSelector(state => state.user.loadingUser);
  const { isLoading: loadingIsUserValidated, data: isUserValidated } =
    useSelector(state => state.user.checkValidated);
  const intervalRef = useRef(null);

  const {
    children,
    requireAuth = false,
    requirePasswordChange = false,
    requireConfirmRegistration = false,
    requiredGroups = [],
  } = props;
  const isAuthenticated = userStatus === 200;
  const mustConfirmRegistration = isUserValidated === false;
  const hasRequiredGroups = requiredGroups.length > 0;
  const loading =
    loadingLocales || checkingAuth || loadingUser || loadingIsUserValidated;

  /**
   * @summary Validates authentication.
   * @description Dispatches a user check when the component mounts if the route requires authentication.
   * Once the user is confirmed to be authenticated,
   * dispatches `getUser()` and `getIsUserValidated()` to retrieve additional account details such as
   * groups.
   * AuthGuard uses groups to enable role-based access control (RBAC).
   */
  useEffect(() => {
    if (requireAuth === true) {
      dispatch(
        checkUserLogged(navigate, authStatusResult => {
          if (authStatusResult === true) {
            dispatch(checkIsValidated());
            dispatch(getUser());
          }
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requireAuth]);

  /**
   * @summary Periodically revalidates auth.
   * @description
   * If authenticated, re-checks the session every `REACT_APP_SESSION_CHECK_TIME` ms
   * to maintain session validity. Clears the interval on unmount.
   */
  useEffect(() => {
    const shouldRun =
      requireAuth === true &&
      isAuthenticated === true &&
      mustChangePassword === false &&
      isUserValidated === true;

    if (shouldRun === true) {
      intervalRef.current = setInterval(() => {
        dispatch(checkUser(navigate));
      }, process.env.REACT_APP_SESSION_CHECK_TIME);
    }

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requireAuth, isAuthenticated, mustChangePassword, isUserValidated]);

  /**
   * @summary Redirects the current user to their default home page based on their role.
   * @returns {JSX.Element} A `<Navigate />` element from `react-router-dom` to perform the redirect.
   */
  function redirectToHome() {
    if (mustChangePassword === true) {
      return <Navigate to="/new-password" replace />;
    }
    if (isAdminUser(groups) === true) {
      return <Navigate to="/admin" replace />;
    }
    if (isFundingAdminUser(groups) === true) {
      return <Navigate to="/fundingadminpage" replace />;
    }
    if (mustConfirmRegistration === true) {
      return <Navigate to="/confirm-registration" replace />;
    }
    return <Navigate to="/fundingpage" replace />;
  }

  /**
   * @description Show loading while:
   * - auth is required and checking if user is authenticated or
   * - auth is required and loading if user is validated (confirmed her/his registration) or
   * - auth is required and loading user data or
   * - auth is required and loading locales from App/Header dispatched action or
   * - auth is not required and loading locales (locales are loaded for all auth cases (should be loaded only once on App.js))
   */
  if (
    (requireAuth === true && loading === true) ||
    (requireAuth === false && loadingLocales === true)
  ) {
    return (
      <div style={styles.spinner}>
        <Spin size="large" />
      </div>
    );
  }

  /**
   * @summary Redirect unauthenticated users.
   */
  if (requireAuth === true && isAuthenticated === false) {
    return <Navigate to="/loginAs" replace />;
  }
  /**
   * @summary Redirect authenticated users.
   */
  if (requireAuth === false && isAuthenticated === true) {
    return redirectToHome();
  }

  /**
   * @summary Password Change Flow Control for Authenticated(always) Users.
   * @description
   * Implements bidirectional password change enforcement:
   * - Forces users who need password change to /new-password route
   * - Prevents users who don't need password change from accessing /new-password
   * This creates a "password change jail" until the requirement is satisfied.
   */
  if (requireAuth === true && isAuthenticated === true) {
    /**
     * User needs to change password but is on a regular route
     * → Force redirect to password change page
     */
    if (requirePasswordChange === false && mustChangePassword === true) {
      return <Navigate to="/new-password" replace />; // Redirect TO password change page
    }
    /**
     * User doesn't need to change password but is on password change route
     * → Redirect to appropriate home page (prevents manual URL manipulation)
     */
    if (requirePasswordChange === true && mustChangePassword === false) {
      return redirectToHome(); // Redirect AWAY from password change page
    }
  }

  /**
   * @summary Confirm Registration Flow Control for Authenticated(always) Users.
   * @description
   * Implements bidirectional confirm regoistration enforcement:
   * - Forces users who need to confirm their regoistration to /confirm-registration route
   * - Prevents users who don't need confirm registration from accessing /confirm-registration
   * This creates a "confirm registration jail" until the requirement is satisfied.
   */
  if (requireAuth === true && isAuthenticated === true) {
    /**
     * User needs to confirm registration but is on a regular route
     * → Force redirect to confirm registration page
     */
    if (
      requireConfirmRegistration === false &&
      mustConfirmRegistration === true
    ) {
      return <Navigate to="/confirm-registration" replace />; // Redirect TO password change page
    }
    /**
     * User doesn't need to confirm registration but is on confirm registration route
     * → Redirect to appropriate home page (prevents manual URL manipulation)
     */
    if (
      requireConfirmRegistration === true &&
      mustConfirmRegistration === false
    ) {
      return redirectToHome(); // Redirect AWAY from confirm registration page
    }
  }

  /**
   * @summary Enforces Access Control.
   * @description
   * If required groups are specified, only allows rendering if the user
   * belongs to at least one of those groups.
   * For groups, the requiredGroups are referring to group identifiers(group.id).
   */
  if (requireAuth === true && isAuthenticated === true) {
    // If no groups required to access this route, aka empty array, continue
    if (hasRequiredGroups === true) {
      const userMeetsGroupRequirement = requiredGroups.some(requiredGroup =>
        (groups ?? []).some(
          group => normalizeString(group.id) === normalizeString(requiredGroup)
        )
      );
      if (userMeetsGroupRequirement === false) {
        return redirectToHome();
      }
    }
  }

  return children;
}

/**
 * @description Normalizes string; aka trims it and makes it to lowercase
 * in order to be handled for group ids.
 * This is done to avoid any mistyping between constant values of group ids.
 * @param {string} [arg=''] - String to be trimmed and converted to lowercase.
 * @returns {string} Normalized string; trimmed and converted to lowercase.
 */
function normalizeString(arg = '') {
  return arg.trim().toLowerCase();
}

const styles = {
  spinner: {
    position: 'absolute',
    width: '100vw',
    left: '0',
    minHeight: '100dvh',
    boxShadow: '0 0 24px 0 rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
};
